<div class="flex h-full overflow-hidden">

  <!-- ── Left panel: subscriptions list ─────────────────────────────── -->
  <div class="flex min-w-0 flex-1 flex-col overflow-hidden p-5">

    <!-- Page header -->
    <div class="mb-4 flex flex-shrink-0 items-center justify-between">
      <div>
        <h1 class="text-sm font-semibold text-zinc-900">Subscriptions</h1>
        <p class="mt-0.5 text-xs text-zinc-500">
          <ng-container *ngIf="!loading">{{ total }} organization{{ total !== 1 ? 's' : '' }}</ng-container>
          <ng-container *ngIf="loading">Loading…</ng-container>
        </p>
      </div>
      <div class="flex items-center gap-2">
        <span *ngIf="backgroundRefreshing" class="flex items-center gap-1 text-[11px] text-zinc-400">
          <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="1.75" class="animate-spin"></lucide-icon>
          Updating…
        </span>
        <button
          (click)="applyFilters()"
          class="flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-50"
        >
          <lucide-icon name="refresh-cw" [size]="12" [strokeWidth]="1.75"></lucide-icon>
          Refresh
        </button>
      </div>
    </div>

    <!-- Error banner -->
    <div *ngIf="error" class="mb-3 flex flex-shrink-0 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs text-red-700">
      <lucide-icon name="circle-alert" [size]="13" [strokeWidth]="2"></lucide-icon>
      {{ error }}
      <button (click)="applyFilters()" class="ml-auto font-medium underline hover:no-underline">Retry</button>
    </div>

    <!-- Table card -->
    <div class="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white">

      <!-- Filter bar -->
      <div class="flex flex-shrink-0 items-center gap-2 border-b border-zinc-200 px-4 py-2.5">

        <!-- Search -->
        <div class="relative w-48">
          <span class="pointer-events-none absolute inset-y-0 left-0 flex w-7 items-center justify-center">
            <lucide-icon name="search" [size]="12" [strokeWidth]="2" class="text-zinc-400"></lucide-icon>
          </span>
          <input
            type="text"
            [(ngModel)]="searchQuery"
            (keyup.enter)="applyFilters()"
            placeholder="Search organizations…"
            class="h-7 w-full rounded-md border border-zinc-200 bg-zinc-50 pl-7 pr-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:border-violet-400 focus:outline-none focus:ring-1 focus:ring-violet-200"
          />
        </div>

        <!-- Tier filter -->
        <select
          [(ngModel)]="tierFilter"
          (change)="applyFilters()"
          class="h-7 rounded-md border border-zinc-200 bg-zinc-50 px-2 pr-6 text-xs text-zinc-700 focus:border-violet-400 focus:outline-none"
        >
          <option value="">All tiers</option>
          <option value="FREE">Free</option>
          <option value="PRO">Pro</option>
          <option value="ENTERPRISE">Enterprise</option>
        </select>

        <span class="ml-auto text-xs text-zinc-400">{{ total }} result{{ total !== 1 ? 's' : '' }}</span>
      </div>

      <!-- Table -->
      <div class="flex-1 overflow-auto">
        <table class="w-full">
          <thead class="sticky top-0 z-10 bg-zinc-50">
            <tr class="border-b border-zinc-200">
              <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Organization</th>
              <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Tier</th>
              <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Status</th>
              <th class="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Team</th>
              <th class="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Monitored</th>
              <th class="px-4 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-zinc-400">Renewal</th>
              <th class="px-4 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-zinc-400"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-zinc-100">

            <!-- Skeleton rows -->
            <ng-container *ngIf="loading">
              <tr *ngFor="let _ of SKELETON_ROWS" class="animate-pulse">
                <td class="px-4 py-3">
                  <div class="h-3 w-36 rounded bg-zinc-100"></div>
                  <div class="mt-1.5 h-2.5 w-24 rounded bg-zinc-100"></div>
                </td>
                <td class="px-4 py-3"><div class="h-5 w-14 rounded bg-zinc-100"></div></td>
                <td class="px-4 py-3"><div class="h-5 w-14 rounded bg-zinc-100"></div></td>
                <td class="px-4 py-3 text-right"><div class="ml-auto h-3 w-6 rounded bg-zinc-100"></div></td>
                <td class="px-4 py-3 text-right"><div class="ml-auto h-3 w-8 rounded bg-zinc-100"></div></td>
                <td class="px-4 py-3"><div class="h-3 w-24 rounded bg-zinc-100"></div></td>
                <td class="px-4 py-3 text-right"><div class="ml-auto h-6 w-14 rounded bg-zinc-100"></div></td>
              </tr>
            </ng-container>

            <!-- Data rows -->
            <ng-container *ngIf="!loading">
              <tr
                *ngFor="let sub of subscriptions"
                class="cursor-pointer transition-colors hover:bg-zinc-50/60"
                [class.bg-violet-50]="sub.organizationId === selectedOrgId"
                [class.hover:bg-violet-50]="sub.organizationId === selectedOrgId"
                (click)="openSidebar(sub.organizationId)"
              >
                <!-- Organization -->
                <td class="px-4 py-3">
                  <p class="text-xs font-medium text-zinc-900">{{ sub.organizationName }}</p>
                  <p class="mt-0.5 font-mono text-[10px] text-zinc-400">{{ sub.organizationId }}</p>
                </td>

                <!-- Tier -->
                <td class="px-4 py-3">
                  <span
                    class="rounded px-1.5 py-0.5 text-[11px] font-semibold"
                    [ngClass]="tierBadgeClass(sub.tier)"
                  >{{ sub.tier }}</span>
                </td>

                <!-- Status -->
                <td class="px-4 py-3">
                  <span
                    class="rounded px-1.5 py-0.5 text-[11px] font-semibold"
                    [ngClass]="statusBadgeClass(sub.status)"
                  >{{ statusLabel(sub.status) }}</span>
                </td>

                <!-- Team Size -->
                <td class="px-4 py-3 text-right text-xs font-medium text-zinc-700">
                  {{ sub.teamSize }}
                </td>

                <!-- Monitored Services -->
                <td class="px-4 py-3 text-right">
                  <span class="text-xs font-medium text-zinc-700">{{ sub.monitoredServicesCount }}</span>
                  <span class="text-[10px] text-zinc-400"> / {{ tierLimit(sub.tier) }}</span>
                </td>

                <!-- Next Renewal -->
                <td class="px-4 py-3 text-xs text-zinc-500">{{ formatDate(sub.nextRenewalDate) }}</td>

                <!-- Open sidebar indicator -->
                <td class="px-4 py-3 text-right">
                  <lucide-icon
                    name="chevron-right"
                    [size]="13"
                    [strokeWidth]="1.75"
                    class="ml-auto transition-colors"
                    [class.text-violet-500]="sub.organizationId === selectedOrgId"
                    [class.text-zinc-300]="sub.organizationId !== selectedOrgId"
                  ></lucide-icon>
                </td>
              </tr>

              <!-- Empty state -->
              <tr *ngIf="subscriptions.length === 0 && !error">
                <td colspan="7" class="px-4 py-16 text-center">
                  <lucide-icon name="credit-card" [size]="28" [strokeWidth]="1.25" class="mx-auto mb-3 text-zinc-300"></lucide-icon>
                  <p class="text-xs font-medium text-zinc-500">No subscriptions found</p>
                  <p class="mt-1 text-[11px] text-zinc-400">
                    {{ searchQuery || tierFilter ? 'No organizations match the current filters.' : 'No organizations have been created yet.' }}
                  </p>
                </td>
              </tr>
            </ng-container>

          </tbody>
        </table>
      </div>

      <!-- Load more -->
      <div *ngIf="nextPageToken && !loading" class="flex flex-shrink-0 justify-center border-t border-zinc-100 px-4 py-3">
        <button
          (click)="loadMore()"
          class="text-xs font-medium text-violet-600 hover:text-violet-700"
        >
          Load more
        </button>
      </div>

    </div>
  </div>

  <!-- ── Right panel: Organization Context Sidebar ───────────────────── -->
  <!-- Width transitions from 0 → 24rem when an org is selected.         -->
  <!-- overflow-hidden hides content during the collapsed state.          -->
  <div
    class="flex-shrink-0 overflow-hidden border-l border-zinc-200 transition-all duration-200"
    [class.w-0]="!selectedOrgId"
    [class.w-96]="!!selectedOrgId"
  >
    <app-org-context-sidebar
      *ngIf="selectedOrgId"
      [orgId]="selectedOrgId"
      (closed)="closeSidebar()"
      (subscriptionSaved)="onSubscriptionSaved($event)"
    ></app-org-context-sidebar>
  </div>

</div>
